import type { HTMLAttributes, ReactNode } from 'react';

import { ScrollArea } from '../scroll-area';

/**
 * Props for the Menu component
 */
export interface MenuProps extends HTMLAttributes<HTMLDivElement> {
  /** Menu items */
  children: ReactNode;
  /** Size variant for menu items */
  size?: 'sm' | 'md';
  /** Maximum height for the items area in pixels (enables scrolling) */
  maxHeight?: number;
  /** Empty state content when no children */
  emptyState?: ReactNode;
}

/**
 * Menu component - container for MenuItem components
 *
 * Can be used as a standalone flat menu or inside Dropdown.
 *
 * @example
 * ```tsx
 * <Menu>
 *   <MenuItem onClick={handleEdit}>Edit</MenuItem>
 *   <MenuItem onClick={handleDuplicate}>Duplicate</MenuItem>
 *   <MenuSeparator />
 *   <MenuItem destructive onClick={handleDelete}>Delete</MenuItem>
 * </Menu>
 *
 * // With scroll
 * <Menu maxHeight={200}>
 *   <MenuItem>Item 1</MenuItem>
 *   <MenuItem>Item 2</MenuItem>
 *   ...
 * </Menu>
 * ```
 */
export function Menu({
  children,
  size: _size = 'md',
  maxHeight,
  emptyState,
  className = '',
  ...props
}: MenuProps) {
  const hasChildren = Array.isArray(children) ? children.filter(Boolean).length > 0 : !!children;

  return (
    <div className={`flex flex-col ${className}`} role="menu" {...props}>
      {maxHeight ? (
        <ScrollArea orientation="vertical" maxHeight={maxHeight} scrollbarPadding>
          <div className="py-pb-1">
            <div className="flex flex-col">{hasChildren ? children : emptyState}</div>
          </div>
        </ScrollArea>
      ) : (
        <div className="py-pb-1">
          <div className="flex flex-col">{hasChildren ? children : emptyState}</div>
        </div>
      )}
    </div>
  );
}
